/**
 * Picks a program's location from the shared `locations` catalog, and manages the catalog in
 * place: add a new room, rename one, delete an unused one.
 *
 * The catalog is shared on purpose — many programs meet in one room — so a rename here
 * renames it for every program in that room, and the hint under the input says so. Delete
 * is refused by the database (ON DELETE RESTRICT) while any program still uses the room;
 * that error is shown as-is rather than pre-checked, since the FK is the real rule.
 */

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import { supabase } from '../supabase';
import type { ProgramLocation } from '../../types/location';
import { LOCATIONS_QUERY_KEY, useAdminLocations } from '../utils/useAdminLocations';

type Mode = { kind: 'pick' } | { kind: 'add' } | { kind: 'rename' } | { kind: 'confirm-delete' };

interface LocationSelectProps {
  /** Selected `locations.id`, or '' for none. */
  value: string;
  onChange: (locationId: string) => void;
  /** Called after a rename or delete, so the caller can refetch rows that embed the name. */
  onCatalogChanged?: () => void;
}

const INPUT =
  'focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md';
const LINK_BUTTON =
  'inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-400';

export function LocationSelect({ value, onChange, onCatalogChanged }: LocationSelectProps) {
  const queryClient = useQueryClient();
  const { data: locations = [], isLoading } = useAdminLocations();
  const [mode, setMode] = useState<Mode>({ kind: 'pick' });
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = locations.find(l => l.id === value) ?? null;

  const reset = () => {
    setMode({ kind: 'pick' });
    setDraft('');
    setError(null);
  };

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: LOCATIONS_QUERY_KEY });
  };

  async function handleAdd() {
    const name = draft.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    // Reuse an existing room spelled the same way rather than failing on the unique index:
    // the admin wanted that room, and it already exists.
    const existing = locations.find(l => l.name.trim().toLowerCase() === name.toLowerCase());
    if (existing) {
      onChange(existing.id);
      setBusy(false);
      reset();
      return;
    }
    const { data, error: insertError } = await supabase
      .from('locations')
      .insert({ name })
      .select('id, name')
      .single();
    setBusy(false);
    if (insertError || !data) {
      setError(insertError?.code === '23505'
        ? `A location named "${name}" already exists.`
        : insertError?.message ?? 'Could not add the location.');
      return;
    }
    await refresh();
    onChange((data as ProgramLocation).id);
    reset();
  }

  async function handleRename() {
    const name = draft.trim();
    if (!selected || !name || name === selected.name) { reset(); return; }
    setBusy(true);
    setError(null);
    const { error: updateError } = await supabase
      .from('locations')
      .update({ name, updated_at: new Date().toISOString() })
      .eq('id', selected.id);
    setBusy(false);
    if (updateError) {
      setError(updateError.code === '23505'
        ? `A location named "${name}" already exists.`
        : updateError.message);
      return;
    }
    await refresh();
    onCatalogChanged?.();
    reset();
  }

  async function handleDelete() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    const { error: deleteError } = await supabase.from('locations').delete().eq('id', selected.id);
    setBusy(false);
    if (deleteError) {
      // 23503: still referenced by a program. Unsaved form state does not count, so the
      // program being edited may be the one holding it.
      setError(deleteError.code === '23503'
        ? `"${selected.name}" is still used by at least one program. Move those programs to another location first.`
        : deleteError.message);
      setMode({ kind: 'pick' });
      return;
    }
    onChange('');
    await refresh();
    onCatalogChanged?.();
    reset();
  }

  return (
    <div>
      <label htmlFor="location" className="flex items-center gap-1 text-sm font-medium text-gray-700">
        <MapPin className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
        Location
      </label>

      {mode.kind === 'add' || mode.kind === 'rename' ? (
        <div className="mt-1 flex gap-2">
          <input
            id="location"
            type="text"
            autoFocus
            className={INPUT}
            placeholder='e.g. "Kaleido Room" or "Annex Room 2"'
            value={draft}
            maxLength={80}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              // Inside the program <form>: Enter must not submit the whole program.
              if (e.key === 'Enter') {
                e.preventDefault();
                void (mode.kind === 'add' ? handleAdd() : handleRename());
              } else if (e.key === 'Escape') {
                reset();
              }
            }}
          />
          <button
            type="button"
            disabled={busy || !draft.trim()}
            onClick={() => void (mode.kind === 'add' ? handleAdd() : handleRename())}
            className="px-3 py-1.5 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300"
          >
            {mode.kind === 'add' ? 'Add' : 'Save'}
          </button>
          <button
            type="button"
            onClick={reset}
            className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      ) : (
        <select
          id="location"
          className={`mt-1 ${INPUT}`}
          value={value}
          disabled={isLoading}
          onChange={e => { setError(null); onChange(e.target.value); }}
        >
          <option value="">— No location —</option>
          {locations.map(l => (
            <option key={l.id} value={l.id}>{l.name}</option>
          ))}
          {/* A selected id the list does not have yet (still loading) must not blank out. */}
          {value && !selected && !isLoading && <option value={value}>(unknown location)</option>}
        </select>
      )}

      {mode.kind === 'confirm-delete' && selected ? (
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-700">
          <span>Delete &ldquo;{selected.name}&rdquo; from the location list?</span>
          <button type="button" disabled={busy} onClick={() => void handleDelete()} className="font-medium text-red-600 hover:text-red-800">
            Delete
          </button>
          <button type="button" onClick={reset} className="font-medium text-gray-600 hover:text-gray-800">
            Keep
          </button>
        </div>
      ) : mode.kind === 'pick' ? (
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <button type="button" className={LINK_BUTTON} onClick={() => { setDraft(''); setError(null); setMode({ kind: 'add' }); }}>
            <Plus className="h-3 w-3" /> New location
          </button>
          {selected && (
            <>
              <button type="button" className={LINK_BUTTON} onClick={() => { setDraft(selected.name); setError(null); setMode({ kind: 'rename' }); }}>
                <Pencil className="h-3 w-3" /> Rename
              </button>
              <button type="button" className={LINK_BUTTON} onClick={() => { setError(null); setMode({ kind: 'confirm-delete' }); }}>
                <Trash2 className="h-3 w-3" /> Delete
              </button>
            </>
          )}
        </div>
      ) : (
        <p className="mt-1 text-xs text-gray-500">
          {mode.kind === 'rename'
            ? 'Renames this location for every program that meets there.'
            : 'Adds it to the shared list, so other programs can pick it too.'}
        </p>
      )}

      {error && <p className="mt-1 text-xs text-red-600" role="alert">{error}</p>}
    </div>
  );
}
